/** Certificate HTML generation — default layout seed + DB template fill + QR/logo. */
import { ProcessedCertificate, COURSE_CONFIG } from '../types';
import { supabaseReviewStorage } from '../services/supabaseStorage';
import { certificateTemplateService } from '../services/certificateTemplateService';
import { courseConfigurationService } from '../services/courseConfigurationService';
import { BRAND } from '../lib/brand';
import {
  buildCertificatePlaceholderMap,
  calculateCertificateExpiryDate,
  formatCertificateDateEnIE,
  formatLocalIsoDate,
  replaceCertificatePlaceholders,
  resolveValidityMonths,
} from './certificatePlaceholders';
import { isAllocatedCertificateNumber } from './certificateNumber';
import QRCode from 'qrcode';

export const CERTIFICATE_LOGO_PATH = BRAND.logoUrl as const;

export const CERT_LAYOUT_MARKER = 'data-cert-layout="2026-09"';

/** Default layout seed for Supabase `certificate_templates` (same idea as email template defaults). */
export const DEFAULT_CERTIFICATE_HTML = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>CRM Certificate Template</title>
  <style>
    * { box-sizing: border-box; }
    html, body { margin: 0; min-height: 100%; background: #e7e7e7; }
    body { padding: 24px; }
    .page.certificate-page {
      position: relative;
      display: block;
      width: 210mm;
      height: 297mm;
      margin: 0 auto;
      overflow: hidden;
      background: #fff;
      box-shadow: 0 2px 16px rgba(0,0,0,.18);
    }
    .page.certificate-page > svg {
      display: block;
      width: 100%;
      height: 100%;
    }
    .cert-logo {
      position: absolute;
      left: 50.34mm;
      top: 30.88mm;
      width: 109.27mm;
      height: 44.71mm;
      object-fit: contain;
      pointer-events: none;
      z-index: 2;
    }
    .cert-qr {
      position: absolute;
      left: 175.0mm;
      top: 262.0mm;
      width: 24.47mm;
      height: 24.47mm;
      object-fit: contain;
      background: #fff;
      pointer-events: none;
      z-index: 2;
      image-rendering: pixelated;
    }
    @page { size: A4 portrait; margin: 0; }
    @media print {
      html, body { width: 210mm; height: 297mm; background: #fff; }
      body { padding: 0; }
      .page.certificate-page { margin: 0; box-shadow: none; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    }
  </style>
</head>
<body>
<div class="page certificate-page" data-cert-layout="2026-09" role="img" aria-label="Training certificate">
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
     width="210mm" height="297mm" viewBox="0 0 595.304 841.89">
  <defs>
    <pattern id="watermark" width="227" height="42" patternUnits="userSpaceOnUse"
             patternTransform="rotate(-45 297.652 420.945)">
      <text font-family="Arial, Helvetica, sans-serif" font-size="12" fill="#215868" opacity="0.15"
            letter-spacing=".35" x="0" y="22">
        <tspan>Ireland T&amp;T</tspan><tspan dx="2" dy="-6">Ireland T&amp;T</tspan><tspan dx="2" dy="6">Ireland T&amp;T</tspan>
      </text>
    </pattern>
    <style>
      .script { font-family: Georgia, "Times New Roman", serif; font-style: italic; }
      .serif { font-family: Georgia, "Times New Roman", serif; }
      .sans { font-family: Arial, Helvetica, sans-serif; }
      .teal { fill: #215868; }
    </style>
  </defs>

  <rect width="595.304" height="841.89" fill="#fff"/>
  <rect x="0" y="0" width="595.304" height="841.89" fill="url(#watermark)"/>
  <rect x="28.346" y="28.346" width="538.612" height="785.198"
        fill="none" stroke="#356864" stroke-width="1.5" opacity="0.72"/>

  <rect x="137.777" y="82.5" width="319.75" height="136.65"
      fill="#fff" stroke="#215868" stroke-width="1.5"
      rx="3.75" ry="3.75"/>

  <text x="297.652" y="248.5" class="script" font-size="18" text-anchor="middle">This is to certify that</text>
  <text x="297.652" y="277.05" class="serif" font-size="20" font-weight="700"
        text-anchor="middle" data-fit="310" data-min-size="12">{{ContactName}}</text>
  <text x="297.652" y="303.025" class="script" font-size="18" text-anchor="middle"
        data-wrap="470" data-max-lines="2" data-line-height="20">{{CompletionStatement}}</text>
  <text x="297.652" y="330.125" class="sans" font-size="20" font-weight="700"
        text-anchor="middle" data-fit="470" data-min-size="12"
        data-wrap="470" data-max-lines="3" data-line-height="22">{{CourseName}}</text>
  <text x="300.741" y="348.7" class="sans" font-size="14" font-weight="400" text-anchor="middle">course</text>
  <text x="95.692" y="390" class="sans" font-size="12" text-anchor="start"
        data-wrap="400" data-max-lines="14" data-line-height="15" data-wrap-align="top">{{training_description}}</text>

  <text x="56.8" y="649.5" class="serif" font-size="18">Instructor:</text>
  <text x="155" y="649.5" class="serif" font-size="18" font-style="italic"
        data-fit="285" data-min-size="11">{{InstructorFirstName}} {{InstructorLastName}}</text>

  <text x="56.8" y="677.9" class="sans" font-size="12" font-weight="700">Date:</text>
  <text id="courseDate" x="95" y="677.9" class="sans" font-size="12" font-weight="700"
        data-fit="170" data-min-size="9">{{CourseDate}}</text>

  <text x="344.8" y="677.9" class="sans" font-size="12" font-weight="700">Expiry date:</text>
  <text id="expiryDate" x="425" y="677.9" class="sans" font-size="12" font-weight="700"
        data-fit="115" data-min-size="9">{{expiry_date}}</text>

  <text x="56.8" y="705.5" class="sans" font-size="12" font-weight="700">Certificate Number:</text>
  <text x="190" y="705.5" class="sans" font-size="12" font-weight="700"
        data-fit="185" data-min-size="9">{{certificate_number}}</text>

  <g class="sans teal" font-size="9">
    <text x="56.8" y="743.2" font-weight="700" text-decoration="underline">IRELAND TRAINING AND TRANSLATIONS T&amp;T Limited</text>
    <text x="56.8" y="753.55"><tspan font-weight="700">Phone:</tspan> 01 442 9118</text>
    <text x="330" y="753.55"><tspan font-weight="700">Email:</tspan> info@irelandtt.com</text>
    <text x="56.8" y="763.9"><tspan font-weight="700">Mobile</tspan> 087 0930808</text>
    <text x="330" y="763.9">www.irelandtt.com</text>
    <text x="56.8" y="774.25"><tspan font-weight="700">Office address:</tspan> 11C Parkmore Industrial Estate, Long Mile Road, Dublin 12</text>
    <text x="56.8" y="784.6">Ireland Training and Translations T&amp;T Limited is registered in Ireland&#160; 540052</text>
  </g>

  <text x="530.775" y="736.5" class="sans" font-size="8" fill="#000" text-anchor="middle">Validate</text>
  <rect x="495.342" y="741.928" width="70.866" height="70.866"
        fill="#fff" stroke="#000" stroke-width="0.75"/>
</svg>
<img class="cert-logo" src="${CERTIFICATE_LOGO_PATH}" alt="Ireland T&amp;T" />
<img class="cert-qr" src="{{QR_Code}}" alt="Scan to verify certificate" />
</div>

<script>
  (() => {
    const fit = (element) => {
      const limit = Number(element.dataset.fit);
      const minimum = Number(element.dataset.minSize || 8);
      let size = Number(element.getAttribute('font-size'));
      while (size > minimum && element.getComputedTextLength() > limit) {
        size -= 0.5;
        element.setAttribute('font-size', size);
      }
    };

    const wrap = (element) => {
      const limit = Number(element.dataset.wrap);
      const maxLines = Number(element.dataset.maxLines || 2);
      const lineHeight = Number(element.dataset.lineHeight || 22);
      const original = element.textContent || '';
      const paragraphs = original.split(/\\n/).map((p) => p.trim()).filter(Boolean);
      if (!paragraphs.length) return;

      const cx = Number(element.getAttribute('x'));
      const baseY = Number(element.getAttribute('y'));
      let fontSize = Number(element.getAttribute('font-size'));
      const anchor = element.getAttribute('text-anchor') || 'middle';
      const ns = 'http://www.w3.org/2000/svg';

      const measure = (text, size) => {
        const t = document.createElementNS(ns, 'text');
        t.setAttribute('font-size', size);
        t.setAttribute('class', element.getAttribute('class') || '');
        t.setAttribute('visibility', 'hidden');
        t.textContent = text;
        element.parentNode.appendChild(t);
        let w = 0;
        try { w = t.getComputedTextLength(); } catch (e) { w = 0; }
        element.parentNode.removeChild(t);
        if (!(w > 0)) w = text.length * size * 0.55;
        return w;
      };

      const buildLines = (size) => {
        const lines = [];
        for (const paragraph of paragraphs) {
          const words = paragraph.split(/\\s+/).filter(Boolean);
          let current = '';
          for (const word of words) {
            const trial = current ? current + ' ' + word : word;
            if (measure(trial, size) <= limit) {
              current = trial;
            } else {
              if (current) lines.push(current);
              current = word;
            }
          }
          if (current) lines.push(current);
        }
        return lines;
      };

      let lines = buildLines(fontSize);
      while (lines.length > maxLines && fontSize > 10) {
        fontSize = Math.max(fontSize - 2, 10);
        element.setAttribute('font-size', fontSize);
        lines = buildLines(fontSize);
        if (fontSize <= 10) break;
      }

      element.textContent = '';
      const align = String(element.dataset.wrapAlign || 'center').toLowerCase();
      const startY = align === 'top' ? baseY : baseY - (lines.length * lineHeight) / 2 + lineHeight / 2;
      lines.forEach((line, i) => {
        const tspan = document.createElementNS(ns, 'tspan');
        tspan.setAttribute('x', cx);
        tspan.setAttribute('y', startY + i * lineHeight);
        tspan.setAttribute('text-anchor', anchor);
        tspan.textContent = line;
        element.appendChild(tspan);
      });
    };

    const runAll = () => {
      document.querySelectorAll('[data-fit]').forEach(fit);
      document.querySelectorAll('[data-wrap]').forEach(wrap);
    };
    if (document.fonts?.ready) document.fonts.ready.then(runAll);
    else runAll();
  })();
</script>
</body>
</html>`;

async function getNextCertificateDisplayNumber(): Promise<string> {
  try {
    const counter = await supabaseReviewStorage.incrementCertificateCounter();
    return `TT-${counter.toString().padStart(6, '0')}`;
  } catch (error) {
    console.error('Error allocating certificate number, using timestamp fallback:', error);
    const timestamp = Date.now().toString().slice(-6);
    return `TT-${timestamp}`;
  }
}

async function fetchAsDataUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch(url, { cache: 'force-cache' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/** Inline logo (+ absolute /images paths) so print / iframe / html2canvas all see it. */
async function inlineCertificateAssets(html: string, origin: string): Promise<string> {
  let out = html;
  if (origin) {
    out = out.split('="/images/').join(`="${origin}/images/`);
    out = out.split("='/images/").join(`='${origin}/images/`);
  }

  const logoAbs = origin ? `${origin}${CERTIFICATE_LOGO_PATH}` : CERTIFICATE_LOGO_PATH;
  const dataUrl = await fetchAsDataUrl(logoAbs);
  if (dataUrl) {
    out = out.split(logoAbs).join(dataUrl);
    out = out.split(CERTIFICATE_LOGO_PATH).join(dataUrl);
  }

  return out;
}

export const generateCertificateHTML = async (certificate: ProcessedCertificate): Promise<string> => {
  const courseConfig = await courseConfigurationService.getByName(certificate.courseName);
  const validityMonths = resolveValidityMonths(certificate, courseConfig);

  let certNumber: string;
  if (isAllocatedCertificateNumber(certificate.id)) {
    certNumber = certificate.id.trim();
  } else {
    certNumber = await getNextCertificateDisplayNumber();
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const verifyUrl = `${origin}/verify/certificate/${encodeURIComponent(certNumber)}`;

  let qrDataUrl = '';
  try {
    qrDataUrl = await QRCode.toDataURL(verifyUrl, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 280,
      color: { dark: '#000000', light: '#ffffff' },
    });
  } catch (e) {
    console.warn('QR generation failed', e);
  }

  const template = await certificateTemplateService.ensureDefaultSeeded();
  let html = template.htmlBody || DEFAULT_CERTIFICATE_HTML;

  if (!html.includes(CERT_LAYOUT_MARKER) && !html.includes("data-cert-layout='2026-09'")) {
    html = DEFAULT_CERTIFICATE_HTML;
  }

  if (!/\bclass=["'][^"']*\bpage\b/.test(html) && /\bcertificate-page\b/.test(html)) {
    html = html.replace(/\bcertificate-page\b/g, 'page certificate-page');
  }

  html = await inlineCertificateAssets(html, origin);

  const placeholders = buildCertificatePlaceholderMap({
    certificate: {
      ...certificate,
      attendeeName: (certificate.attendeeName || '')
        .split(' ')
        .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
        .join(' '),
    },
    courseConfig,
    certificateNumber: certNumber,
    qrDataUrl,
  });

  html = replaceCertificatePlaceholders(html, placeholders);

  if (qrDataUrl) {
    const hasUsableQrImg =
      /\bcert-qr\b[^>]*src=["']data:image\//i.test(html) ||
      /src=["']data:image\/[^"']+["'][^>]*\bcert-qr\b/i.test(html);
    if (!hasUsableQrImg) {
      const qrImg = `<img class="cert-qr" src="${qrDataUrl}" alt="Scan to verify certificate" style="position:absolute;left:175mm;top:262mm;width:24.47mm;height:24.47mm;object-fit:contain;background:#fff;z-index:2;" />`;
      if (/class=["'][^"']*\bcertificate-page\b/i.test(html) && /<\/div>/i.test(html)) {
        html = html.replace(
          /(<div[^>]*class=["'][^"']*\bcertificate-page\b[^"']*["'][^>]*>)([\s\S]*?)(<\/div>)/i,
          `$1$2${qrImg}$3`
        );
      } else if (/<\/body>/i.test(html)) {
        html = html.replace(/<\/body>/i, `${qrImg}</body>`);
      } else {
        html += qrImg;
      }
    }
  }

  (certificate as any).__allocatedCertificateNumber = certNumber;
  const expiryComputed = calculateCertificateExpiryDate(certificate, validityMonths);
  (certificate as any).__computedExpiryDate = formatCertificateDateEnIE(expiryComputed);
  (certificate as any).__computedExpiryDateISO = formatLocalIsoDate(expiryComputed);

  certificate.id = certNumber;

  return html;
};

export function calculateExpiryDateDisplay(
  certificate: ProcessedCertificate,
  validityMonths?: number
): string {
  const months =
    validityMonths ||
    (COURSE_CONFIG[certificate.courseName as keyof typeof COURSE_CONFIG] as { validityMonths?: number } | undefined)
      ?.validityMonths ||
    36;
  return formatCertificateDateEnIE(calculateCertificateExpiryDate(certificate, months));
}
